import { Phone } from 'lucide-react';
import { MessageIcon, WhatsAppIcon } from '@/components/property/ContactIcons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MobileContactActionBarProps {
  phone: string;
  email?: string;
  variant?: 'inline' | 'fixed';
  onEmailClick?: () => void;
}

/**
 * 3-button contact bar (Message · Call · WhatsApp).
 * - `inline`: rendered below the header, full-width row.
 * - `fixed`: pinned to the bottom of the viewport on mobile/tablet.
 */
export function MobileContactActionBar({
  phone,
  email,
  variant = 'inline',
  onEmailClick,
}: MobileContactActionBarProps) {
  const cleanPhone = (phone || '').replace(/[^0-9+]/g, '');
  const waPhone = (phone || '').replace(/[^0-9]/g, '');

  const handleEmail = () => {
    if (onEmailClick) {
      onEmailClick();
    } else if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      // Scroll to inline lead form section
      document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const wrapperClass =
    variant === 'fixed'
      ? 'lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background border-t shadow-lg px-3 py-2'
      : 'w-full';

  return (
    <div className={wrapperClass}>
      <div className="grid grid-cols-3 gap-2">
        <Button
          className={cn(
            'h-11 gap-2 font-semibold text-sm text-primary-foreground',
            'bg-primary hover:bg-primary/90',
          )}
          asChild
          aria-label="Call"
        >
          <a href={`tel:${cleanPhone}`}>
            <Phone className="h-4 w-4" />
            <span>Call</span>
          </a>
        </Button>
        <Button
          variant="outline"
          className="h-11 gap-2 font-semibold text-sm border-primary/30 hover:bg-primary/5"
          asChild
          aria-label="WhatsApp"
        >
          <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer">
            <WhatsAppIcon className="h-4 w-4" />
            <span>WhatsApp</span>
          </a>
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 gap-2 font-semibold text-sm text-primary border-primary/30 hover:bg-primary/5"
          onClick={handleEmail}
          aria-label="Message"
        >
          <MessageIcon className="h-4 w-4" />
          <span>Message</span>
        </Button>
      </div>
    </div>
  );
}
